import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area,
} from 'recharts';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  CalculatorIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  PlusIcon,
  MapPinIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import api from '../lib/api';
import { farmApi } from '../lib/api';
import { Card, CardHeader, CardTitle, StatCard, EmptyState, Skeleton } from '../components/ui';
import { ChartSkeleton } from '../components/ui/Skeleton';

// ============ TYPES ============

interface MarketPrice {
  id: string;
  commodity: string;
  commodityName: string;
  market: string;
  price: number;
  unit: string;
  date: string;
}

interface CommodityInfo {
  key: string;
  name: string;
  avgPrice: number;
  unit: string;
  markets: string[];
  category: string;
}

interface PriceTrend {
  direction: string;
  strength: number;
  volatility: number;
}

interface RevenueScenario {
  pricePerUnit: number;
  totalRevenue: number;
}

interface RevenueForcast {
  cropType: string;
  areaHectares: number;
  predictedYieldKgHa: number;
  totalYieldKg: number;
  market: string;
  currentPricePerUnit: number;
  unit: string;
  scenarios: {
    low: RevenueScenario;
    expected: RevenueScenario;
    high: RevenueScenario;
  };
  lastSeasonComparison: {
    lastSeasonPrice: number;
    priceChange: number;
    priceChangePercent: number;
  };
  currency: string;
}

interface InputCost {
  id: string;
  farmId: string;
  season: string;
  category: string;
  item: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  date: string;
  notes: string | null;
}

interface CostBreakdownItem {
  category: string;
  totalCost: number;
  percentage: number;
  items: Array<{ item: string; totalCost: number; quantity: number }>;
}

interface CostSummary {
  farmId: string;
  season: string;
  totalCost: number;
  costPerHectare: number | null;
  breakdown: CostBreakdownItem[];
  currency: string;
}

interface ProfitabilityReport {
  farmId: string;
  season: string;
  totalRevenue: number;
  totalCosts: number;
  grossMargin: number;
  netProfit: number;
  profitPerHectare: number | null;
  breakEvenYieldKgHa: number | null;
  roiPercent: number;
  currency: string;
  revenueBreakdown: Array<{ source: string; amount: number }>;
  costBreakdown: Array<{ category: string; amount: number }>;
}

// ============ API FUNCTIONS ============

const marketApi = {
  getPrices: (commodity?: string, market?: string) => {
    const params = new URLSearchParams();
    if (commodity) params.set('commodity', commodity);
    if (market) params.set('market', market);
    return api.get(`/market/prices?${params}`).then(r => r.data);
  },
  getPriceHistory: (commodity: string, market: string, days: number) =>
    api.get(`/market/prices/history/${commodity}?market=${market}&days=${days}`).then(r => r.data),
  getCommodities: () =>
    api.get('/market/commodities').then(r => r.data),
  getMarkets: () =>
    api.get('/market/markets').then(r => r.data),
  forecastRevenue: (data: { cropType: string; areaHectares: number; predictedYieldKgHa: number; market: string }) =>
    api.post('/market/forecast-revenue', data).then(r => r.data),
  recordInputCost: (data: { farmId: string; category: string; item: string; quantity: number; unitCost: number; date?: string; notes?: string }) =>
    api.post('/market/input-costs', data).then(r => r.data),
  getInputCosts: (farmId: string, season?: string) => {
    const params = new URLSearchParams();
    if (season) params.set('season', season);
    return api.get(`/market/input-costs/${farmId}?${params}`).then(r => r.data);
  },
  getCostSummary: (farmId: string, season?: string, areaHectares?: number) => {
    const params = new URLSearchParams();
    if (season) params.set('season', season);
    if (areaHectares) params.set('areaHectares', String(areaHectares));
    return api.get(`/market/cost-summary/${farmId}?${params}`).then(r => r.data);
  },
  getProfitability: (farmId: string, season?: string, areaHectares?: number, cropType?: string) => {
    const params = new URLSearchParams();
    if (season) params.set('season', season);
    if (areaHectares) params.set('areaHectares', String(areaHectares));
    if (cropType) params.set('cropType', cropType);
    return api.get(`/market/profitability/${farmId}?${params}`).then(r => r.data);
  },
  getPriceTrends: (commodity: string, market: string, days: number) =>
    api.get(`/market/price-trends/${commodity}?market=${market}&days=${days}`).then(r => r.data),
  getPriceComparison: (commodity: string) =>
    api.get(`/market/price-comparison/${commodity}`).then(r => r.data),
};

// ============ HELPERS ============

const COLORS = ['#16a34a', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'];

function formatKES(amount: number): string {
  if (amount >= 1000000) {
    return `KES ${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `KES ${(amount / 1000).toFixed(0)}K`;
  }
  return `KES ${amount.toLocaleString()}`;
}

function formatKESFull(amount: number): string {
  return `KES ${amount.toLocaleString()}`;
}

const TABS = [
  { id: 'prices', label: 'Prices', icon: CurrencyDollarIcon },
  { id: 'revenue', label: 'Revenue', icon: ArrowTrendingUpIcon },
  { id: 'costs', label: 'Costs', icon: CalculatorIcon },
  { id: 'profitability', label: 'Profitability', icon: BanknotesIcon },
] as const;

type TabId = typeof TABS[number]['id'];

// ============ CUSTOM TOOLTIP ============

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; name: string; color: string }>; label?: string }) {
  if (active && payload && payload.length) {
    return (
      <div className="glass-strong rounded-lg px-3 py-2 shadow-soft-md border border-gray-100">
        <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm font-semibold" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? formatKESFull(entry.value) : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
}

// ============ MAIN COMPONENT ============

export default function MarketPrices() {
  const [activeTab, setActiveTab] = useState<TabId>('prices');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Market Intelligence</h1>
        <p className="text-gray-500">Kenya commodity prices, revenue forecasting, and farm economics</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all
              ${activeTab === tab.id
                ? 'bg-white text-kenagri-700 shadow-soft-sm'
                : 'text-gray-500 hover:text-gray-700'
              }
            `}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'prices' && <PricesTab />}
      {activeTab === 'revenue' && <RevenueTab />}
      {activeTab === 'costs' && <CostsTab />}
      {activeTab === 'profitability' && <ProfitabilityTab />}
    </div>
  );
}

// ============ PRICES TAB ============

function PricesTab() {
  const [selectedMarket, setSelectedMarket] = useState('Nairobi');
  const [selectedCommodity, setSelectedCommodity] = useState('maize');
  const [historyDays, setHistoryDays] = useState(90);

  const { data: commoditiesRes } = useQuery({
    queryKey: ['market-commodities'],
    queryFn: marketApi.getCommodities,
  });

  const { data: marketsRes } = useQuery({
    queryKey: ['market-markets'],
    queryFn: marketApi.getMarkets,
  });

  const { data: pricesRes, isLoading: pricesLoading } = useQuery({
    queryKey: ['market-prices', selectedMarket],
    queryFn: () => marketApi.getPrices(undefined, selectedMarket),
  });

  const { data: historyRes, isLoading: historyLoading } = useQuery({
    queryKey: ['market-history', selectedCommodity, selectedMarket, historyDays],
    queryFn: () => marketApi.getPriceHistory(selectedCommodity, selectedMarket, historyDays),
  });

  const { data: comparisonRes } = useQuery({
    queryKey: ['market-comparison', selectedCommodity],
    queryFn: () => marketApi.getPriceComparison(selectedCommodity),
  });

  const commodities: CommodityInfo[] = commoditiesRes?.data || [];
  const markets: string[] = marketsRes?.data || ['Nairobi', 'Eldoret', 'Nakuru', 'Kisumu', 'Mombasa'];
  const prices: MarketPrice[] = pricesRes?.data || [];
  const historyPrices: MarketPrice[] = historyRes?.data?.prices || [];
  const historyTrend: PriceTrend | null = historyRes?.data?.trend || null;
  const comparison = comparisonRes?.data;

  // Deduplicate prices by commodity (pick first per commodity)
  const uniquePrices = useMemo(() => {
    const seen = new Set<string>();
    return prices.filter(p => {
      if (seen.has(p.commodity)) return false;
      seen.add(p.commodity);
      return true;
    });
  }, [prices]);

  // Price change from avg
  const getPriceChange = (commodity: string, price: number) => {
    const info = commodities.find(c => c.key === commodity);
    if (!info) return null;
    const change = ((price - info.avgPrice) / info.avgPrice) * 100;
    return Math.round(change * 10) / 10;
  };

  // Chart data for history
  const historyChartData = historyPrices.map(p => ({
    date: format(new Date(p.date), 'MMM d'),
    price: p.price,
  }));

  // Chart data for market comparison
  const comparisonChartData = comparison?.markets?.map((m: { market: string; price: number }) => ({
    market: m.market,
    price: m.price,
  })) || [];

  const selectedCommodityInfo = commodities.find(c => c.key === selectedCommodity);

  return (
    <div className="space-y-6">
      {/* Market Selector */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <MapPinIcon className="h-5 w-5 text-gray-400" />
          <select
            value={selectedMarket}
            onChange={e => setSelectedMarket(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
          >
            {markets.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <TagIcon className="h-5 w-5 text-gray-400" />
          <select
            value={selectedCommodity}
            onChange={e => setSelectedCommodity(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
          >
            {commodities.map(c => (
              <option key={c.key} value={c.key}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Price Cards */}
      {pricesLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-card p-4">
              <Skeleton width="60%" height={14} />
              <Skeleton width="40%" height={28} className="mt-2" />
              <Skeleton width="50%" height={16} className="mt-2" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {uniquePrices.slice(0, 12).map(p => {
            const change = getPriceChange(p.commodity, p.price);
            return (
              <button
                key={p.commodity}
                onClick={() => setSelectedCommodity(p.commodity)}
                className={`
                  text-left bg-white rounded-xl border shadow-card p-4
                  hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200
                  ${selectedCommodity === p.commodity ? 'border-kenagri-500 ring-1 ring-kenagri-500' : 'border-gray-100'}
                `}
              >
                <p className="text-sm font-medium text-gray-500">{p.commodityName}</p>
                <p className="mt-1 text-xl font-bold text-gray-900">
                  {formatKESFull(p.price)}
                </p>
                <div className="mt-1 flex items-center gap-1">
                  <span className="text-xs text-gray-400">/{p.unit}</span>
                  {change !== null && (
                    <span className={`
                      ml-auto inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium
                      ${change >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}
                    `}>
                      {change >= 0 ? (
                        <ArrowTrendingUpIcon className="h-3 w-3" />
                      ) : (
                        <ArrowTrendingDownIcon className="h-3 w-3" />
                      )}
                      {Math.abs(change)}%
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Price History Chart */}
        <Card variant="gradient">
          <CardHeader>
            <CardTitle>
              {selectedCommodityInfo?.name || 'Price'} History - {selectedMarket}
            </CardTitle>
            <div className="flex gap-1">
              {[30, 90, 180].map(d => (
                <button
                  key={d}
                  onClick={() => setHistoryDays(d)}
                  className={`
                    px-2.5 py-1 rounded-md text-xs font-medium transition-colors
                    ${historyDays === d
                      ? 'bg-kenagri-100 text-kenagri-700'
                      : 'text-gray-500 hover:bg-gray-100'
                    }
                  `}
                >
                  {d}d
                </button>
              ))}
            </div>
          </CardHeader>
          <div className="h-72">
            {historyLoading ? (
              <div className="h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kenagri-600" />
              </div>
            ) : historyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={historyChartData}>
                  <defs>
                    <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#16a34a" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="price" name="Price (KES)" stroke="#16a34a" strokeWidth={2} fill="url(#priceGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                No price history available
              </div>
            )}
          </div>
          {historyTrend && (
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              <span className={`
                inline-flex items-center gap-1 px-2 py-1 rounded-full font-medium
                ${historyTrend.direction === 'rising' ? 'bg-green-50 text-green-700' :
                  historyTrend.direction === 'falling' ? 'bg-red-50 text-red-700' :
                  'bg-gray-50 text-gray-600'}
              `}>
                {historyTrend.direction === 'rising' ? <ArrowTrendingUpIcon className="h-3 w-3" /> :
                 historyTrend.direction === 'falling' ? <ArrowTrendingDownIcon className="h-3 w-3" /> :
                 <MinusIcon className="h-3 w-3" />}
                Trend: {historyTrend.direction}
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                Volatility: {historyTrend.volatility}%
              </span>
            </div>
          )}
        </Card>

        {/* Market Comparison Chart */}
        <Card variant="gradient">
          <CardHeader>
            <CardTitle>{selectedCommodityInfo?.name || 'Commodity'} - Market Comparison</CardTitle>
          </CardHeader>
          <div className="h-72">
            {comparisonChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="market" tick={{ fontSize: 11, fill: '#9ca3af' }} width={80} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="price" name="Price (KES)" fill="#16a34a" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                Select a commodity to compare markets
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ============ REVENUE TAB ============

function RevenueTab() {
  const [selectedFarmId, setSelectedFarmId] = useState('');
  const [selectedCrop, setSelectedCrop] = useState('maize');
  const [selectedMarket, setSelectedMarket] = useState('Nairobi');
  const [yieldInput, setYieldInput] = useState('2500');
  const [areaInput, setAreaInput] = useState('');

  const { data: farms = [] } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  const { data: commoditiesRes } = useQuery({
    queryKey: ['market-commodities'],
    queryFn: marketApi.getCommodities,
  });

  const commodities: CommodityInfo[] = commoditiesRes?.data || [];

  // Auto-select first farm
  const farmId = selectedFarmId || farms[0]?.id || '';
  const selectedFarm = farms.find(f => f.id === farmId);
  const area = areaInput ? parseFloat(areaInput) : (selectedFarm?.areaHectares || 10);

  const { data: forecastRes, isLoading: forecastLoading } = useQuery({
    queryKey: ['market-forecast', selectedCrop, area, yieldInput, selectedMarket],
    queryFn: () => marketApi.forecastRevenue({
      cropType: selectedCrop,
      areaHectares: area,
      predictedYieldKgHa: parseFloat(yieldInput) || 2500,
      market: selectedMarket,
    }),
    enabled: area > 0,
  });

  const forecast: RevenueForcast | null = forecastRes?.data || null;

  const scenarioData = forecast ? [
    { scenario: 'Low', revenue: forecast.scenarios.low.totalRevenue, fill: '#ef4444' },
    { scenario: 'Expected', revenue: forecast.scenarios.expected.totalRevenue, fill: '#16a34a' },
    { scenario: 'High', revenue: forecast.scenarios.high.totalRevenue, fill: '#3b82f6' },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Farm</label>
            <select
              value={farmId}
              onChange={e => {
                setSelectedFarmId(e.target.value);
                setAreaInput('');
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
            >
              {farms.length === 0 && <option value="">No farms</option>}
              {farms.map(f => (
                <option key={f.id} value={f.id}>{f.name} ({f.areaHectares?.toFixed(1)} ha)</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Crop</label>
            <select
              value={selectedCrop}
              onChange={e => setSelectedCrop(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
            >
              {commodities.map(c => (
                <option key={c.key} value={c.key}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Market</label>
            <select
              value={selectedMarket}
              onChange={e => setSelectedMarket(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
            >
              {['Nairobi', 'Eldoret', 'Nakuru', 'Kisumu', 'Mombasa'].map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Area (ha)</label>
            <input
              type="number"
              value={areaInput || (selectedFarm?.areaHectares?.toFixed(1) ?? '')}
              onChange={e => setAreaInput(e.target.value)}
              placeholder="e.g. 10"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Yield (kg/ha)</label>
            <input
              type="number"
              value={yieldInput}
              onChange={e => setYieldInput(e.target.value)}
              placeholder="e.g. 2500"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
            />
          </div>
        </div>
      </Card>

      {forecastLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ChartSkeleton height={120} />
          <ChartSkeleton height={120} />
          <ChartSkeleton height={120} />
        </div>
      ) : forecast ? (
        <>
          {/* Revenue Forecast Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-red-100 shadow-card p-5">
              <p className="text-sm font-medium text-red-600">Low Scenario</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{formatKES(forecast.scenarios.low.totalRevenue)}</p>
              <p className="mt-1 text-xs text-gray-500">@ {formatKESFull(forecast.scenarios.low.pricePerUnit)}/kg</p>
            </div>
            <div className="bg-gradient-to-br from-kenagri-50 to-white rounded-xl border border-kenagri-200 shadow-card p-5">
              <p className="text-sm font-medium text-kenagri-700">Expected Revenue</p>
              <p className="mt-1 text-2xl font-bold text-kenagri-800">{formatKES(forecast.scenarios.expected.totalRevenue)}</p>
              <p className="mt-1 text-xs text-gray-500">@ {formatKESFull(forecast.scenarios.expected.pricePerUnit)}/kg</p>
            </div>
            <div className="bg-white rounded-xl border border-blue-100 shadow-card p-5">
              <p className="text-sm font-medium text-blue-600">High Scenario</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{formatKES(forecast.scenarios.high.totalRevenue)}</p>
              <p className="mt-1 text-xs text-gray-500">@ {formatKESFull(forecast.scenarios.high.pricePerUnit)}/kg</p>
            </div>
          </div>

          {/* Revenue Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Scenario Chart */}
            <Card variant="gradient">
              <CardHeader>
                <CardTitle>Revenue Scenarios</CardTitle>
              </CardHeader>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={scenarioData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="scenario" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="revenue" name="Revenue (KES)" radius={[6, 6, 0, 0]}>
                      {scenarioData.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Forecast Summary */}
            <Card variant="gradient">
              <CardHeader>
                <CardTitle>Forecast Details</CardTitle>
              </CardHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Total Yield</p>
                    <p className="text-lg font-bold">{forecast.totalYieldKg.toLocaleString()} kg</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Current Price</p>
                    <p className="text-lg font-bold">{formatKESFull(forecast.currentPricePerUnit)}</p>
                    <p className="text-xs text-gray-400">per {forecast.unit}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Area</p>
                    <p className="text-lg font-bold">{forecast.areaHectares} ha</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Market</p>
                    <p className="text-lg font-bold">{forecast.market}</p>
                  </div>
                </div>
                {/* Last Season Comparison */}
                <div className="p-4 rounded-lg border border-gray-100">
                  <p className="text-sm font-medium text-gray-700 mb-2">vs Last Season</p>
                  <div className="flex items-center gap-3">
                    <span className={`
                      inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-semibold
                      ${forecast.lastSeasonComparison.priceChangePercent >= 0
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-700'
                      }
                    `}>
                      {forecast.lastSeasonComparison.priceChangePercent >= 0 ? (
                        <ArrowTrendingUpIcon className="h-4 w-4" />
                      ) : (
                        <ArrowTrendingDownIcon className="h-4 w-4" />
                      )}
                      {forecast.lastSeasonComparison.priceChangePercent > 0 ? '+' : ''}
                      {forecast.lastSeasonComparison.priceChangePercent}%
                    </span>
                    <span className="text-sm text-gray-500">
                      Last season: {formatKESFull(forecast.lastSeasonComparison.lastSeasonPrice)}/kg
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </>
      ) : (
        <EmptyState
          icon={ArrowTrendingUpIcon}
          title="No forecast available"
          description="Select a farm and crop to generate revenue forecasts."
        />
      )}
    </div>
  );
}

// ============ COSTS TAB ============

function CostsTab() {
  const queryClient = useQueryClient();
  const [selectedFarmId, setSelectedFarmId] = useState('');
  const [costForm, setCostForm] = useState({
    category: 'seeds',
    item: '',
    quantity: '',
    unitCost: '',
    notes: '',
  });

  const { data: farms = [] } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  const farmId = selectedFarmId || farms[0]?.id || '';
  const selectedFarm = farms.find(f => f.id === farmId);

  const { data: costsRes } = useQuery({
    queryKey: ['market-costs', farmId],
    queryFn: () => marketApi.getInputCosts(farmId),
    enabled: !!farmId,
  });

  const { data: summaryRes, isLoading: summaryLoading } = useQuery({
    queryKey: ['market-cost-summary', farmId, selectedFarm?.areaHectares],
    queryFn: () => marketApi.getCostSummary(farmId, undefined, selectedFarm?.areaHectares),
    enabled: !!farmId,
  });

  const costs: InputCost[] = costsRes?.data || [];
  const summary: CostSummary | null = summaryRes?.data || null;

  const recordCostMutation = useMutation({
    mutationFn: (data: { farmId: string; category: string; item: string; quantity: number; unitCost: number; notes?: string }) =>
      marketApi.recordInputCost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-costs'] });
      queryClient.invalidateQueries({ queryKey: ['market-cost-summary'] });
      setCostForm({ category: 'seeds', item: '', quantity: '', unitCost: '', notes: '' });
    },
  });

  const handleSubmitCost = (e: React.FormEvent) => {
    e.preventDefault();
    if (!farmId || !costForm.item || !costForm.quantity || !costForm.unitCost) return;

    recordCostMutation.mutate({
      farmId,
      category: costForm.category,
      item: costForm.item,
      quantity: parseFloat(costForm.quantity),
      unitCost: parseFloat(costForm.unitCost),
      notes: costForm.notes || undefined,
    });
  };

  // Pie chart data from summary
  const pieData = summary?.breakdown.map(b => ({
    name: b.category.charAt(0).toUpperCase() + b.category.slice(1),
    value: b.totalCost,
  })) || [];

  // Monthly cost data (group costs by month)
  const monthlyCostData = useMemo(() => {
    const monthMap = new Map<string, number>();
    for (const cost of costs) {
      const month = format(new Date(cost.date), 'MMM yyyy');
      monthMap.set(month, (monthMap.get(month) || 0) + cost.totalCost);
    }
    return Array.from(monthMap.entries())
      .map(([month, total]) => ({ month, total }))
      .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());
  }, [costs]);

  const categories = ['seeds', 'fertilizer', 'pesticides', 'labor', 'fuel', 'machinery', 'irrigation', 'transport', 'storage', 'other'];

  return (
    <div className="space-y-6">
      {/* Farm Selector */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-500">Farm:</label>
          <select
            value={farmId}
            onChange={e => setSelectedFarmId(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
          >
            {farms.length === 0 && <option value="">No farms</option>}
            {farms.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Total Costs"
          value={summary ? formatKES(summary.totalCost) : 'KES 0'}
          icon={CalculatorIcon}
          color="amber"
        />
        <StatCard
          title="Cost per Hectare"
          value={summary?.costPerHectare ? formatKES(summary.costPerHectare) : 'N/A'}
          icon={ChartBarIcon}
          color="blue"
        />
        <StatCard
          title="Cost Categories"
          value={summary?.breakdown.length || 0}
          icon={TagIcon}
          color="purple"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Cost Form */}
        <Card variant="gradient">
          <CardHeader>
            <CardTitle>Record Input Cost</CardTitle>
          </CardHeader>
          <form onSubmit={handleSubmitCost} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                <select
                  value={costForm.category}
                  onChange={e => setCostForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
                >
                  {categories.map(c => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Item</label>
                <input
                  type="text"
                  value={costForm.item}
                  onChange={e => setCostForm(prev => ({ ...prev, item: e.target.value }))}
                  placeholder="e.g. DAP fertilizer"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Quantity</label>
                <input
                  type="number"
                  value={costForm.quantity}
                  onChange={e => setCostForm(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder="e.g. 5"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Unit Cost (KES)</label>
                <input
                  type="number"
                  value={costForm.unitCost}
                  onChange={e => setCostForm(prev => ({ ...prev, unitCost: e.target.value }))}
                  placeholder="e.g. 5500"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={costForm.notes}
                onChange={e => setCostForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="e.g. Bought from local agrovet"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
              />
            </div>
            {costForm.quantity && costForm.unitCost && (
              <div className="p-3 bg-kenagri-50 rounded-lg text-sm">
                <span className="text-gray-600">Total: </span>
                <span className="font-bold text-kenagri-800">
                  {formatKESFull(parseFloat(costForm.quantity) * parseFloat(costForm.unitCost))}
                </span>
              </div>
            )}
            <button
              type="submit"
              disabled={!farmId || recordCostMutation.isPending}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-kenagri-600 text-white rounded-lg text-sm font-medium hover:bg-kenagri-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="h-4 w-4" />
              {recordCostMutation.isPending ? 'Recording...' : 'Record Cost'}
            </button>
            {recordCostMutation.isSuccess && (
              <p className="text-sm text-green-600 text-center">Cost recorded successfully!</p>
            )}
          </form>
        </Card>

        {/* Cost Breakdown Pie Chart */}
        <Card variant="gradient">
          <CardHeader>
            <CardTitle>Cost Breakdown</CardTitle>
          </CardHeader>
          <div className="h-72">
            {summaryLoading ? (
              <div className="h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kenagri-600" />
              </div>
            ) : pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={50}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatKESFull(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <CalculatorIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p>No costs recorded yet</p>
                  <p className="text-xs mt-1">Use the form to add input costs</p>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Monthly Cost Timeline */}
      {monthlyCostData.length > 0 && (
        <Card variant="gradient">
          <CardHeader>
            <CardTitle>Monthly Cost Timeline</CardTitle>
          </CardHeader>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyCostData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="total" name="Costs (KES)" fill="#f59e0b" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Recent Costs Table */}
      {costs.length > 0 && (
        <Card variant="gradient">
          <CardHeader>
            <CardTitle>Recent Costs</CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Unit Cost</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody>
                {costs.slice(0, 10).map(cost => (
                  <tr key={cost.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-600">{format(new Date(cost.date), 'MMM d, yyyy')}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {cost.category}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">{cost.item}</td>
                    <td className="py-3 px-4 text-right text-gray-600">{cost.quantity}</td>
                    <td className="py-3 px-4 text-right text-gray-600">{formatKESFull(cost.unitCost)}</td>
                    <td className="py-3 px-4 text-right font-semibold text-gray-900">{formatKESFull(cost.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============ PROFITABILITY TAB ============

function ProfitabilityTab() {
  const [selectedFarmId, setSelectedFarmId] = useState('');

  const { data: farms = [] } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  const farmId = selectedFarmId || farms[0]?.id || '';
  const selectedFarm = farms.find(f => f.id === farmId);

  const { data: profitRes, isLoading: profitLoading } = useQuery({
    queryKey: ['market-profitability', farmId, selectedFarm?.areaHectares, selectedFarm?.cropType],
    queryFn: () => marketApi.getProfitability(
      farmId,
      undefined,
      selectedFarm?.areaHectares,
      selectedFarm?.cropType || undefined,
    ),
    enabled: !!farmId,
  });

  const report: ProfitabilityReport | null = profitRes?.data || null;

  const costBreakdownData = report?.costBreakdown.map(b => ({
    name: b.category.charAt(0).toUpperCase() + b.category.slice(1),
    value: b.amount,
  })) || [];

  // ROI gauge percentage (clamped to 0-200 for display)
  const roiDisplay = report ? Math.min(Math.max(report.roiPercent, -100), 200) : 0;
  const roiGaugePercent = ((roiDisplay + 100) / 300) * 100; // map -100..200 to 0..100

  return (
    <div className="space-y-6">
      {/* Farm Selector */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-500">Farm:</label>
          <select
            value={farmId}
            onChange={e => setSelectedFarmId(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kenagri-500"
          >
            {farms.length === 0 && <option value="">No farms</option>}
            {farms.map(f => (
              <option key={f.id} value={f.id}>{f.name} - {f.cropType || 'Unknown crop'}</option>
            ))}
          </select>
        </div>
        {report && (
          <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
            Season: {report.season}
          </span>
        )}
      </div>

      {profitLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-card p-6">
              <Skeleton width="60%" height={14} />
              <Skeleton width="40%" height={28} className="mt-2" />
            </div>
          ))}
        </div>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Revenue"
              value={formatKES(report.totalRevenue)}
              icon={BanknotesIcon}
              color="green"
            />
            <StatCard
              title="Total Costs"
              value={formatKES(report.totalCosts)}
              icon={CalculatorIcon}
              color="amber"
            />
            <StatCard
              title="Gross Margin"
              value={formatKES(report.grossMargin)}
              icon={ChartBarIcon}
              color={report.grossMargin >= 0 ? 'blue' : 'rose'}
            />
            <StatCard
              title="Net Profit"
              value={formatKES(report.netProfit)}
              icon={CurrencyDollarIcon}
              color={report.netProfit >= 0 ? 'green' : 'rose'}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ROI & Key Metrics */}
            <Card variant="gradient">
              <CardHeader>
                <CardTitle>Return on Investment</CardTitle>
              </CardHeader>
              <div className="space-y-6">
                {/* ROI Gauge */}
                <div className="text-center">
                  <div className="relative w-48 h-24 mx-auto overflow-hidden">
                    {/* Gauge background */}
                    <div className="absolute inset-0 rounded-t-full border-8 border-b-0 border-gray-100" />
                    {/* Gauge fill */}
                    <div
                      className="absolute inset-0 rounded-t-full border-8 border-b-0 transition-all duration-1000"
                      style={{
                        borderColor: report.roiPercent >= 50 ? '#16a34a' : report.roiPercent >= 0 ? '#f59e0b' : '#ef4444',
                        clipPath: `inset(0 ${100 - Math.min(roiGaugePercent, 100)}% 0 0)`,
                      }}
                    />
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
                      <p className={`text-3xl font-bold ${report.roiPercent >= 0 ? 'text-kenagri-700' : 'text-red-600'}`}>
                        {report.roiPercent}%
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {report.roiPercent >= 50 ? 'Excellent return' :
                     report.roiPercent >= 20 ? 'Good return' :
                     report.roiPercent >= 0 ? 'Marginal return' :
                     'Loss - below break-even'}
                  </p>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Profit per Hectare</p>
                    <p className={`text-lg font-bold ${(report.profitPerHectare || 0) >= 0 ? 'text-kenagri-700' : 'text-red-600'}`}>
                      {report.profitPerHectare !== null ? formatKES(report.profitPerHectare) : 'N/A'}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Break-even Yield</p>
                    <p className="text-lg font-bold text-gray-900">
                      {report.breakEvenYieldKgHa !== null ? `${report.breakEvenYieldKgHa.toLocaleString()} kg/ha` : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Cost Breakdown Chart */}
            <Card variant="gradient">
              <CardHeader>
                <CardTitle>Cost Breakdown</CardTitle>
              </CardHeader>
              <div className="h-72">
                {costBreakdownData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={costBreakdownData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        innerRadius={50}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {costBreakdownData.map((_, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatKESFull(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <CalculatorIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                      <p>No cost data available</p>
                      <p className="text-xs mt-1">Record costs in the Costs tab</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Revenue vs Costs Comparison */}
          <Card variant="gradient">
            <CardHeader>
              <CardTitle>Revenue vs Costs Breakdown</CardTitle>
            </CardHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Revenue Side */}
              <div>
                <h4 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-1">
                  <ArrowTrendingUpIcon className="h-4 w-4" />
                  Revenue Sources
                </h4>
                {report.revenueBreakdown.length > 0 ? (
                  <div className="space-y-2">
                    {report.revenueBreakdown.map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <span className="text-sm text-gray-700">{item.source}</span>
                        <span className="text-sm font-bold text-green-700">{formatKESFull(item.amount)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-3 bg-green-100 rounded-lg border border-green-200">
                      <span className="text-sm font-bold text-green-800">Total Revenue</span>
                      <span className="text-sm font-bold text-green-800">{formatKESFull(report.totalRevenue)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No revenue data (estimated from market prices)</p>
                )}
              </div>

              {/* Costs Side */}
              <div>
                <h4 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-1">
                  <CalculatorIcon className="h-4 w-4" />
                  Cost Categories
                </h4>
                {report.costBreakdown.length > 0 ? (
                  <div className="space-y-2">
                    {report.costBreakdown.map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <span className="text-sm text-gray-700">{item.category.charAt(0).toUpperCase() + item.category.slice(1)}</span>
                        <span className="text-sm font-bold text-amber-700">{formatKESFull(item.amount)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-3 bg-amber-100 rounded-lg border border-amber-200">
                      <span className="text-sm font-bold text-amber-800">Total Costs</span>
                      <span className="text-sm font-bold text-amber-800">{formatKESFull(report.totalCosts)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No costs recorded yet</p>
                )}
              </div>
            </div>
          </Card>
        </>
      ) : farms.length === 0 ? (
        <EmptyState
          icon={BanknotesIcon}
          title="No farms available"
          description="Add a farm to view profitability analysis."
          action={{ label: 'Add Farm', href: '/farms/new' }}
        />
      ) : (
        <EmptyState
          icon={BanknotesIcon}
          title="Select a farm"
          description="Choose a farm above to view its profitability report."
        />
      )}
    </div>
  );
}
